import { LmsRouteNames } from '~/types/types';

export interface RouteMetadata {
  routeName: string;
  labelGenerator: (params: Record<string, any>, data?: any) => string;
  entityType?: 'category' | 'contributor' | 'course' | 'lesson' | 'level';
  breadcrumbable: boolean;
  parentRoute?: string;
  /** Route maintains context from previous navigation (doesn't start new section) */
  contextual?: boolean;
}

export function humanizeSlug(slug: string): string {
  if (!slug) return '';

  return slug
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

export const ROUTE_METADATA: Record<string, RouteMetadata> = {
  [LmsRouteNames.home]: {
    routeName: LmsRouteNames.home,
    labelGenerator: () => 'Home',
    breadcrumbable: false,
  },

  [LmsRouteNames.courseCategories]: {
    routeName: LmsRouteNames.courseCategories,
    labelGenerator: () => 'Course Categories',
    breadcrumbable: true,
  },

  [LmsRouteNames.courseCategory]: {
    routeName: LmsRouteNames.courseCategory,
    labelGenerator: (params, data) => {
      return data?.title || humanizeSlug(params.slug);
    },
    entityType: 'category',
    breadcrumbable: true,
    parentRoute: LmsRouteNames.courseCategories,
  },

  [LmsRouteNames.contributors]: {
    routeName: LmsRouteNames.contributors,
    labelGenerator: () => 'Contributors',
    breadcrumbable: true,
  },

  [LmsRouteNames.contributorDetails]: {
    routeName: LmsRouteNames.contributorDetails,
    labelGenerator: (params, data) => {
      return data?.name || humanizeSlug(params.slug);
    },
    entityType: 'contributor',
    breadcrumbable: true,
    parentRoute: LmsRouteNames.contributors,
  },

  [LmsRouteNames.courseDetails]: {
    routeName: LmsRouteNames.courseDetails,
    labelGenerator: (params, data) => {
      return data?.title ? `Course: ${data.title}` : 'Course';
    },
    entityType: 'course',
    breadcrumbable: true,
    contextual: true, // Maintains context from previous navigation
    parentRoute: LmsRouteNames.allContent, // Fallback when no contextual parent (direct link)
  },

  [LmsRouteNames.lesson]: {
    routeName: LmsRouteNames.lesson,
    labelGenerator: (params, data) => {
      return data?.title ? `Lesson: ${data.title}` : 'Lesson';
    },
    entityType: 'lesson',
    breadcrumbable: true,
    contextual: true, // Maintains context from course navigation chain
  },

  [LmsRouteNames.allContent]: {
    routeName: LmsRouteNames.allContent,
    labelGenerator: () => 'All Content',
    breadcrumbable: true,
  },

  [LmsRouteNames.byLevel]: {
    routeName: LmsRouteNames.byLevel,
    labelGenerator: () => 'Browse by Level',
    breadcrumbable: true,
  },

  [LmsRouteNames.levelCourses]: {
    routeName: LmsRouteNames.levelCourses,
    labelGenerator: (params, data) => {
      const levelTitles: Record<string, string> = {
        introductory: 'Introductory Courses',
        beginner: 'Beginner Courses',
        intermediate: 'Intermediate Courses',
        advanced: 'Advanced Courses',
      };
      return data?.pageTitle || levelTitles[params.slug] || humanizeSlug(params.slug) + ' Courses';
    },
    entityType: 'level',
    breadcrumbable: true,
    parentRoute: LmsRouteNames.byLevel,
  },
};
